import React, { Component } from 'react'
import moment from 'moment'
import { Modal } from 'semantic-ui-react'
import { isBlankNullOrUndefined } from '../../utils/validations'
import { sendReportGox, sendReportCollections } from "../../redux/modules/billing"
import ModalHeader from './ModalComponents/ModalHeader'
import ModalContent from './ModalComponents/ModalContent'
import ModalActions from './ModalComponents/ModalActions'


export default class BillingDetailsModal extends Component {

  getDepositDates = () => {
    const { startDate } = this.props;
    let response = []
    let friday = moment(startDate).add(1, 'M').startOf('month').day("Friday");
    if (friday.date() > 7) friday.add(7, 'd');
    const month = friday.month();
    while (month === friday.month()) {
      response.push(friday.format('dddd MMM DD,YYYY'));
      friday.add(7, 'd');
    }
    return response
  }

  showProductionPayDetails = () => this.props.contractType === 'Collections' ? this.processCollectionsContract() : this.processProductionContract();

  processProductionContract = () => {
    const { reportData, contractType, prodAdjWo, scheduleNotes, calendarPayList, GoxList, getPracticeName, formatCurrency, earnings } = this.props;

    let tableData = [];
    const earningsPerPractice = {};

    const productionByDate = reportData?.production?.orderByDate ?? {};

    for (const dateKey of Object.keys(productionByDate)) {
      for (const practice of Object.keys(productionByDate[dateKey])) {
        const dayPay = calendarPayList?.find(p => moment(p.start).isSame(dateKey, 'day') && p.practice === practice)?.price ?? 0;
        const goxForDay = GoxList?.filter(gox => gox.practice === practice && gox.dateOfService === dateKey)
          .reduce((sum, gox) => sum + gox.bCare, 0) ?? 0;
        const financialNotesForDay = scheduleNotes?.filter(n => n.dateString === dateKey && n.practice.toLowerCase() === practice.toLowerCase())
          .reduce((sum, note) => sum + (+note.financialNotes), 0) ?? 0;

        const prod = productionByDate[dateKey][practice]?.price ?? 0;
        const adj = reportData?.adjustment?.orderByDate?.[dateKey]?.[practice]?.price ?? 0;
        const wo = reportData?.writeoff?.orderByDate?.[dateKey]?.[practice]?.price ?? 0;

        let billableCare = (prod + adj - wo);
        if (contractType === 'GOZ' || contractType === 'GOX') {
          billableCare -= goxForDay;
        }
        billableCare *= (prodAdjWo * 0.01);

        const dayEarnings = (dayPay || 0) + financialNotesForDay;
        const practiceName = getPracticeName(practice) === 'Not found' ? practice : getPracticeName(practice);
        earningsPerPractice[practiceName] = (earningsPerPractice[practiceName] || 0) + dayEarnings;

        tableData.push({
          date: dateKey,
          practiceName,
          production: prod,
          adjustment: adj,
          writeOff: wo,
          gox: goxForDay,
          billableCare,
          financialNotes: financialNotesForDay,
          earnings: dayEarnings,
          isNote: false,
        });
      }
    }

    const daysWithProduction = new Set(Object.keys(productionByDate));
    (scheduleNotes ?? [])
      .filter(note => !daysWithProduction.has(note.dateString))
      .forEach(({ dateString, practice, bcare, financialNotes }) => {
        tableData.push({
          date: dateString,
          practiceName: practice,
          production: 0, adjustment: 0, writeOff: 0, gox: 0,
          billableCare: bcare ?? 0,
          financialNotes: +financialNotes,
          earnings: +financialNotes,
          isNote: true,
        });
      });

    const totals = tableData.reduce((acc, row) => {
      acc.production += row.production;
      acc.adjustment += row.adjustment;
      acc.writeOff += row.writeOff;
      acc.gox += row.gox;
      acc.billableCare += row.billableCare;
      acc.financialNotes += row.financialNotes;
      return acc;
    }, { production: 0, adjustment: 0, writeOff: 0, gox: 0, billableCare: 0, financialNotes: 0 });

    const productionTable = tableData
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((row, i) => (
        <tr key={`prod-${row.date}-${row.practiceName}-${i}`}>
          <td>{row.date}</td>
          <td>{row.practiceName}</td>
          <td>{formatCurrency(row.production)}</td>
          <td>{formatCurrency(row.adjustment)}</td>
          <td>{formatCurrency(row.writeOff)} {row.writeOff}</td>
          {(contractType === 'GOX' || contractType === 'GOZ') && <td>{formatCurrency(row.gox)}</td>}
          <td>{formatCurrency(row.billableCare)}</td>
          <td>{formatCurrency(row.financialNotes)}</td>
          <td>{formatCurrency(row.earnings)}</td>
        </tr>
      ));

    productionTable.push(
      <tr key="prod-total">
        <td><strong>Total</strong></td><td />
        <td><b>{formatCurrency(totals.production)}</b></td>
        <td><b>{formatCurrency(totals.adjustment)}</b></td>
        <td><b>{formatCurrency(totals.writeOff)}</b></td>
        {(contractType === 'GOX' || contractType === 'GOZ') && <td><b>{formatCurrency(totals.gox)}</b></td>}
        <td><b>{formatCurrency(totals.billableCare)}</b></td>
        <td><b>{formatCurrency(totals.financialNotes)}</b></td>
        <td><b>{formatCurrency(earnings)}</b></td>
      </tr>
    );

    return { productionTableData: tableData, productionTable, earningsPerPractice };
  };

  processCollectionsContract = () => {
    const { formatCurrency, earnings } = this.props;
    let tableData = [];
    const earningsPerPractice = {};

    const totals = tableData.reduce((acc, row) => {
      acc.collection += row.collection;
      acc.financialNotes += row.financialNotes;
      return acc;
    }, { collection: 0, financialNotes: 0 });

    const productionTable = tableData
      .sort((a, b) => a.date.localeCompare(b.date))
      .map((row, i) => (
        <tr key={`coll-${row.date}-${row.practiceName}-${i}`}>
          <td>{row.date}</td>
          <td>{row.practiceName}</td>
          <td>{formatCurrency(row.collection)}</td>
          <td>{formatCurrency(row.financialNotes)}</td>
          <td>{formatCurrency(row.earnings)}</td>
        </tr>
      ));

    productionTable.push(
      <tr key="coll-total">
        <td><strong>Total</strong></td><td />
        <td><b>{formatCurrency(totals.collection)}</b></td>
        <td><b>{formatCurrency(totals.financialNotes)}</b></td>
        <td><b>{formatCurrency(earnings)}</b></td>
      </tr>
    );

    return { productionTableData: tableData, productionTable, earningsPerPractice };
  };

  showDepositsPractices = (returnType) => {
    const { reportData, getPracticeName } = this.props;
    let depositTable = [];
    let depositTableData = {};

    if (reportData && reportData.production) {
      Object.keys(reportData.production).forEach(practice => {
        practice !== 'price' && practice !== 'orderByDate' && practice !== 'reportData' && depositTable.push(
          <td key={`showDepositPracticeCell${practice}`}>{getPracticeName(practice)}</td>
        )
        if (practice !== 'price' && practice !== 'orderByDate' && practice !== 'reportData') {
          depositTableData[practice] = {
            practiceName: getPracticeName(practice),
            practice: practice
          }
        }
      })
      depositTableData.payCheck = {
        practiceName: 'Total',
        practice: 'Total'
      }
      depositTable.push(<td key={`showDepositGrandTotalCell`} >
        {'Grand Total'}
      </td>)
    }

    const returnObject = { depositTable, depositTableData }
    return returnType.length > 0 ? returnObject[returnType] : depositTable;
  }

  showDepositsAmounts = (pay) => {
    const { reportData, formatCurrency } = this.props;
    let depositTableData = {}
    const payCheck = pay / 2;

    if (reportData && reportData.production) {
      Object.keys(reportData.production).forEach(practice => {
        const practicePay = payCheck / (Object.keys(reportData.production).length - 3);
        if (practice !== 'price' && practice !== 'orderByDate' && practice !== 'reportData') {
          depositTableData[practice] = {
            practicePay: formatCurrency(practicePay),
            practice: this.props.getPracticeName(practice)
          }
        }
      })
      depositTableData.payCheck = {
        practicePay: formatCurrency(payCheck),
        practice: 'Total'
      }
    }

    return depositTableData;
  }

  showTimeOffRequests = (returnType) => {
    const { timeOffRequestData, monthlyBaseWorkDays } = this.props
    let timeOffRow;
    let timeOffRowData;

    const requested = timeOffRequestData.filter(({ requestType }) => requestType === "requested").reduce((total, obj) => obj.days + total, 0)
    const requestedApproved = timeOffRequestData.filter(({ requestType, status }) => requestType === "requested" && status === 1).reduce((total, obj) => obj.days + total, 0)
    const forced = timeOffRequestData.filter(({ requestType }) => requestType === "forced").reduce((total, obj) => obj.days + total, 0)
    const forcedApproved = timeOffRequestData.filter(({ requestType, status }) => requestType === "forced" && status === 1).reduce((total, obj) => obj.days + total, 0)

    timeOffRow = (<tr>
      <td>{requested}</td>
      <td>{requestedApproved}</td>
      <td>{forced}</td>
      <td>{forcedApproved}</td>
      <td>{monthlyBaseWorkDays}</td>
    </tr>)
    timeOffRowData = {
      requested,
      requestedApproved,
      forced,
      forcedApproved,
      monthlyBaseWorkDays
    }

    const returnObject = { timeOffRow, timeOffRowData }
    return returnType.length > 0 ? returnObject[returnType] : timeOffRow;
  }

  showFinancialNotes = (returnType) => {
    const { scheduleNotes, formatCurrency, unFormatCurrency } = this.props;
    let financialNotesTable = [];
    let financialNotesTableData = [];
    if (scheduleNotes && scheduleNotes.length) {
      let total = 0
      scheduleNotes.forEach((scheduleNote, scheduleKey) => {
        total += parseFloat(scheduleNote.financialNotes) || 0;
        financialNotesTableData.push(scheduleNote)
        financialNotesTable.push(
          <tr key={`financialNoteRow${scheduleKey}`}>
            <td>{scheduleNote.notesType}</td>
            <td>{scheduleNote.dateString}</td>
            <td>{scheduleNote.title}</td>
            <td>{scheduleNote.practice}</td>
            <td>{scheduleNote.dentist}</td>
            <td>{scheduleNote.desc}</td>
            <td>{formatCurrency(unFormatCurrency(scheduleNote.financialNotes))}</td>
          </tr>
        )
      })
      financialNotesTable.push(
        <tr key={`financialNoteRowTotal`}>
          <td colSpan={6}><strong>Total</strong></td>
          <td>{formatCurrency(total)}</td>
        </tr>
      )
    }
    const returnObject = { financialNotesTable, financialNotesTableData }
    return returnType.length > 0 ? returnObject[returnType] : financialNotesTable;
  }

  sendGoxReport = (contractType) => {
    const { dispatch, startDate, doctorName } = this.props;
    const subTitle = ` X-Ray ${contractType} (`
    const title = doctorName + subTitle + moment(startDate).format('MMMM YYYY') + ")";
    dispatch(sendReportGox(title));
  }

  sendCollectionsReport = (contractType) => {
    const { dispatch, startDate, doctorName, detailsModal } = this.props;
    const subTitle = ` ${contractType} (`
    const title = doctorName + subTitle + moment(startDate).format('MMMM YYYY') + ")";
    dispatch(sendReportCollections(title, detailsModal));
  }

  mergeReportSections = (reportData) => {
    // Helper to safely convert a value to an array
    const safeArray = (arr) => Array.isArray(arr) ? arr : [];

    const combined = {
      production: { reportData: {} },
      writeoff: { reportData: {} },
      adjustment: { reportData: {} },
    };

    const prodData = reportData?.production?.reportData || {};
    const writeoffData = reportData?.writeoff?.reportData || {};
    const adjData = reportData?.adjustment?.reportData || {};

    // Merge production (keep prices as-is)
    Object.keys(prodData).forEach(date => {
      combined.production.reportData[date] = [...safeArray(prodData[date])];
    });

    // Merge writeoff into production (make price negative if positive)
    Object.keys(writeoffData).forEach(date => {
      if (!combined.production.reportData[date]) combined.production.reportData[date] = [];
      combined.production.reportData[date] = [
        ...combined.production.reportData[date],
        ...safeArray(writeoffData[date]).map(item => ({
          ...item,
          price: item.price > 0 ? -item.price : item.price
        }))
      ];
    });

    // Merge adjustment into production (make price negative if positive)
    Object.keys(adjData).forEach(date => {
      if (!combined.production.reportData[date]) combined.production.reportData[date] = [];
      combined.production.reportData[date] = [
        ...combined.production.reportData[date],
        ...safeArray(adjData[date]).map(item => ({
          ...item
        }))
      ];
    });

    // Keep original writeoff and adjustment untouched
    combined.writeoff.reportData = { ...writeoffData };
    combined.adjustment.reportData = { ...adjData };

    return combined.production.reportData;
  };

  prepareTableData = () => {
    const {
      detailsModal,
      reportData,
      reportDetailsData,
      detailsModalType,
      eventDate,
      eventClick,
      familyData,
      nextDaysData,
      collectionGrouped,
      collectionList
    } = this.props

    let tableData = {}
    let collectionData

    // Handle collection type conversion
    if (detailsModalType === 'collection') {
      const colObj = {}
      const colList = []

      if (Object.keys(collectionGrouped).length) {
        Object.keys(collectionGrouped).forEach((key) => {
          colObj[key] = collectionGrouped[key].filter(
            (collection) => collection.ProcDate === eventDate
          )
        })
      }

      if (collectionList.length) {
        colList.push(...collectionList.filter((collection) => collection.ProcDate === eventDate))
      }

      return {
        tableData: null,
        detailsModalType: 'collectionList',
        reportData: { collectionList: colList, collectionObj: colObj }
      }
    }

    // Handle collectionList initial data
    if (detailsModalType === 'collectionList') {
      collectionData = { collectionList, collectionObj: collectionGrouped }
    }

    // Determine tableData based on modal type
    const dataSource = (detailsModal === 'details' ? reportDetailsData : reportData) ?? reportData;

    if (dataSource?.[detailsModalType]?.reportData) {
      tableData = dataSource[detailsModalType].reportData
    } else if (!dataSource) {
      tableData = null
    } else if (detailsModalType === 'familyView' && !familyData?.brokenListChild?.length) {
      tableData = null
    }

    // Filter by event date if needed
    if (eventClick === 'day' && tableData && Object.keys(tableData).length) {
      tableData = tableData[eventDate] ? { [eventDate]: tableData[eventDate] } : {}
    }

    // Handle nextDays and familyCapacityView
    if (['nextDays', 'familyCapacityView'].includes(detailsModalType)) {
      if (nextDaysData?.orderByDate?.[eventDate]?.[nextDaysData.familyPractice]) {
        tableData = nextDaysData.orderByDate[eventDate][nextDaysData.familyPractice]
      }
    }

    return { tableData, collectionData, detailsModalType, reportData }
  }

  preparePayStubData = () => {
    const {
      contractType,
      billingData,
      earnings,
      numberOfDaysWorked,
      rawMonthlyBasePay,
      payRateOverBase,
      rawMonthlyBaseProduction,
      prodAdjWo,
      startDate,
      doctorName,
      dentistWorkTimeType,
      patientVisits,
      rampPracticeDetails,
      monthlyBaseWorkDays,
      rampEndDate,
      numberOfScheduledDays
    } = this.props

    const { earningsPerPractice, productionTableData, productionTable } = this.showProductionPayDetails()

    const depositDates = this.getDepositDates()
    const depositsPracticesData = this.showDepositsPractices('depositTableData')
    const depositsAmountsData = this.showDepositsAmounts(earnings)
    const timeOffRequests = this.showTimeOffRequests('timeOffRow')
    const timeOffRequestsData = this.showTimeOffRequests('timeOffRowData')
    const financialNotes = this.showFinancialNotes('financialNotesTable')
    const financialNotesData = this.showFinancialNotes('financialNotesTableData')

    let collectionPay = 0
    let dailyRatePay = 0
    let payIsBase = false

    if (contractType === 'Collections' && billingData) {
      dailyRatePay = this.props.dailyRatePay
      payIsBase = this.props.payIsBase
      collectionPay = this.props.collectionPay
    }

    const isRampActive = rampEndDate && new Date(rampEndDate) >= new Date()
    const offeredDays = numberOfScheduledDays + timeOffRequestsData.requestedApproved + timeOffRequestsData.forcedApproved

    return {
      depositDates,
      productionPayDetails: productionTable,
      productionPayDetailsData: productionTableData,
      earningsPerPracticeData: earningsPerPractice,
      depositsPracticesData,
      depositsAmountsData,
      timeOffRequests,
      timeOffRequestsData,
      financialNotes,
      financialNotesData,
      rampOfferedDays: isRampActive ? offeredDays : undefined,
      nonRampOfferedDays: !isRampActive ? offeredDays : undefined,
      details: {
        numberOfDaysWorked,
        earnings,
        productionPayDetailsData: productionTableData,
        rawMonthlyBasePay,
        depositDates,
        depositsPracticesData,
        payRateOverBase,
        rawMonthlyBaseProduction,
        prodAdjWo,
        startDate,
        depositsAmountsData,
        contractType,
        doctorName,
        dentistWorkTimeType,
        patientVisits,
        timeOffRequestsData,
        financialNotesData,
        nonRampOfferedDays: !isRampActive ? offeredDays : undefined,
        rampOfferedDays: isRampActive ? offeredDays : undefined,
        earningsPerPracticeData: earningsPerPractice,
        goxTotal: this.calculateGoxTotal(),
        collectionTotal: this.calculateCollectionTotal(),
        rampPracticeDetails,
        monthlyBaseWorkDays,
        collectionPay,
        dailyRatePay,
        payIsBase
      }
    }
  }

  calculateGoxTotal = () => {
    const goxList = !isBlankNullOrUndefined(this.props.GoxList) && this.props.GoxList.length
      ? this.props.GoxList
      : []

    return goxList.reduce((total, item) => total + item.bCare, 0)
  }

  calculateCollectionTotal = () => {
    const { detailsModal, detailsModalType, contractType, collectionList } = this.props

    if ((detailsModal === 'collection' && detailsModalType === 'payStub') ||
      (detailsModal === 'payStub' && contractType === 'Collections')) {
      return collectionList?.reduce((total, item) => total + item.Amount, 0) ?? 0
    }

    return 0
  }

  prepareCollectionListData = (detailsModal, collectionData, reportData) => {
    if (detailsModal === 'report') {
      const collectionObj = collectionData?.collectionObj ?? reportData.collectionObj
      return { collectionObj, CollectionList: [], tableData: collectionObj }
    }

    const CollectionList = collectionData?.collectionList ?? reportData.collectionList
    return { CollectionList, collectionObj: {}, tableData: CollectionList }
  }

  getTableDataArray = (tableData, detailsModal) => {
    if (detailsModal === 'goxContract') {
      return this.props.GoxList
    }

    const tableDataArray = []
    Object.keys(tableData ?? {}).forEach((key) => {
      tableData[key].forEach((item) => tableDataArray.push(item))
    })

    return tableDataArray
  }

  render() {
    let { detailsModalType, eventClick } = this.props
    const { startDate, eventDate, contractType, earningsReportOptions, detailsModal, collectionList, nextDaysObj, nextDaysData } = this.props

    const doctorName = this.props.doctorName ??
      (this.props.getDoctorFullName && this.props.getDoctorFullName(this.props.docAbbr)) ??
      this.props.docAbbr

    // Prepare table data
    let { tableData, collectionData, reportData } = this.prepareTableData();

    // Handle pay stub data
    let payStubData = {}
    let goxTotal = 0
    let goxList = []
    let collectionTotal = 0

    if (detailsModalType === 'payStub') {
      payStubData = this.preparePayStubData()
    }

    if (detailsModal === 'goxContract' || detailsModalType === 'payStub') {
      goxList = !isBlankNullOrUndefined(this.props.GoxList) && this.props.GoxList.length
        ? this.props.GoxList
        : []
      goxTotal = this.calculateGoxTotal()
    }

    collectionTotal = this.calculateCollectionTotal()

    // Handle collection list
    let CollectionList = []
    let collectionObj = {}

    if (detailsModalType === 'collectionList') {
      const collectionListData = this.prepareCollectionListData(
        detailsModal,
        collectionData,
        reportData || this.props.reportData
      )
      CollectionList = collectionListData.CollectionList
      collectionObj = collectionListData.collectionObj
      tableData = collectionListData.tableData
    }

    // Prepare table data array
    const tableDataArray = this.getTableDataArray(tableData, detailsModal, detailsModalType)

    const reportTotal = (detailsModalType === 'adjustment' || detailsModalType === 'writeoff')
      ? tableDataArray.reduce((sum, item) => sum + (item.price ?? 0), 0)
      : detailsModalType === 'production' && this.props.reportBCareTotal != null
        ? this.props.reportBCareTotal
        : null

    // Normalize eventClick
    if (eventClick === 'today') {
      eventClick = 'day'
    }

    return (
      <Modal
        open={true}
        onClose={() => {
          this.props.hideDetailsModal(detailsModalType)
          if (this.props.onModalClose) {
            this.props.onModalClose({ docAbbr: null, isPayStubSummary: false })
          }
        }}
        style={{ overflow: 'auto' }}
        closeIcon
        size={detailsModalType === 'familyView' ? 'fullscreen' : 'large'}
        className={detailsModalType === 'familyView' ? 'familyView' : ''}
      >
        <ModalHeader
          detailsModalType={detailsModalType}
          contractType={contractType}
          doctorName={doctorName}
          startDate={startDate}
          nextDaysObj={nextDaysObj}
          earningsReportOptions={earningsReportOptions}
          eventClick={eventClick}
          eventDate={eventDate}
          detailsModal={detailsModal}
          reportTotal={reportTotal}
        />

        <Modal.Content scrolling className="pt-0">
          <Modal.Description className="mt20">
            <div className="table-responsive overflow-unset">
              <ModalContent
                detailsModalType={detailsModalType}
                tableData={tableData}
                tableDataArray={tableDataArray}
                detailsModal={detailsModal}
                contractType={contractType}
                earningsReportOptions={earningsReportOptions}
                collectionObj={collectionObj}
                CollectionList={CollectionList}
                goxTotal={goxTotal}
                goxList={goxList}
                collectionTotal={collectionTotal}
                reportBCareTotal={this.props.reportBCareTotal}
                {...this.props}
                {...payStubData}
              />
            </div>
          </Modal.Description>
        </Modal.Content>

        <ModalActions
          detailsModalType={detailsModalType}
          tableData={tableData}
          collectionList={collectionList}
          contractType={contractType}
          detailsModal={detailsModal}
          eventClick={eventClick}
          eventDate={eventDate}
          startDate={startDate}
          details={payStubData.details}
          nextDaysObj={nextDaysObj}
          nextDaysData={nextDaysData}
          sendGoxReport={this.sendGoxReport}
          sendCollectionsReport={this.sendCollectionsReport}
          sendFamilyReport={this.props.sendFamilyReport}
          sendReport={this.props.sendReport}
          onSelectEvent={this.props.onSelectEvent}
        />
      </Modal>
    )
  }
}
